/**
 * Project catalog. Display copy (titles/descriptions) is translated via the
 * featuredProjects.* and op.proj.* i18n keys; the strings here are the
 * English source of truth used for SEO schemas and the AI chat context.
 */
export interface Project {
  id: number;
  title: string;
  slug: string;
  description: string;
  image: string;
  tags: string[];
  demoUrl: string;
  codeUrl: string;
  category: string;
  completedDate: string;
  label?: string;
}

export const projects: Project[] = [
  {
    "id": 1,
    "title": "AI-Powered Projects Website & Interactive Chat System",
    "slug": "ai-powered-projects-chat-system",
    "description": "Developed and integrated comprehensive AI-powered features throughout my projects website, including an intelligent ChatGPT-powered conversation system, AI-enhanced user interactions, and automated content generation. The system features dynamic project showcases, intelligent Q&A capabilities, and seamless integration with OpenAI's GPT models for real-time conversational experiences. Implemented advanced prompt engineering, context management, and responsive AI interfaces that enhance user engagement and demonstrate modern AI integration capabilities.",
    "image": "/AI_Image.png",
    "tags": [
      "AI",
      "OpenAI",
      "Latest GPT",
      "React",
      "TypeScript",
      "API Integration"
    ],
    "demoUrl": "https://talkhaled.com/chat",
    "codeUrl": "https://github.com/tkkalkhaled/Talkhaled.com",
    "category": "ml",
    "label": "AI Engineering",
    "completedDate": "2025-06-10"
  },
  {
    "id": 2,
    "title": "Enterprise-Grade Website Security Implementation",
    "slug": "website-security-implementation",
    "description": "Built and deployed a comprehensive web security system that transformed the site from basic security to A+ grade (110/100) across all major security testing platforms including Mozilla Observatory, SSL Labs, and Security Headers. Implemented comprehensive security headers, automated monitoring systems, and created detailed security documentation. This project demonstrates mastery of web security principles, DevOps practices, and professional deployment standards.",
    "image": "/Security_Image.png",
    "tags": [
      "Security",
      "Netlify",
      "CSP",
      "HSTS",
      "DevOps",
      "Monitoring",
      "SSL/TLS",
      "HTTP Headers"
    ],
    "demoUrl": "https://talkhaled.com",
    "codeUrl": "https://github.com/tkkalkhaled/Talkhaled.com",
    "category": "security",
    "label": "Security Engineering",
    "completedDate": "2025-06-20"
  },
  {
    "id": 3,
    "title": "NOTEorious - Modern Notes App",
    "slug": "modern-notes-app",
    "description": "Developed a comprehensive modern notes application called NOTEorious as a senior design project at Michigan State University for client \"Maruqonda Wyatt\". This full-featured mobile application integrates cutting-edge AI technologies, advanced note-taking capabilities, real-time handwriting recognition, and intelligent document analysis. The project represents a complete end-to-end development cycle, encompassing everything from brand identity creation and logo design to complex backend AI integration and cross-platform mobile development. Built with Flutter for seamless iOS and Android compatibility, the app features a custom LexifAI OCR chatbot, sophisticated handwriting-to-text conversion, animated background systems, and a robust MongoDB backend infrastructure.",
    "image": "/optimized/NOTEorious.webp",
    "tags": [
      "Flutter",
      "AI",
      "OCR",
      "MongoDB",
      "Express.js",
      "Handwriting Recognition",
      "Mobile Development",
      "UI/UX"
    ],
    "demoUrl": "#",
    "codeUrl": "#",
    "category": "web",
    "label": "Mobile App Development",
    "completedDate": "2025-05-13"
  },
  {
    "id": 4,
    "title": "LexifAI - Custom AI OCR Chatbot",
    "slug": "lexifai-ocr-chatbot",
    "description": "Developed LexifAI, a custom AI-powered document analysis engine featuring advanced OCR technology for the NOTEorious app. This intelligent system extracts text from PDFs, images, and documents with 95%+ accuracy while providing conversational AI interactions. Built entirely from scratch with custom machine learning models, LexifAI seamlessly integrates into mobile applications for real-time document processing and analysis.",
    "image": "/optimized/LexifAI.webp",
    "tags": [
      "AI",
      "OCR",
      "Computer Vision",
      "Flutter",
      "Machine Learning",
      "NLP"
    ],
    "demoUrl": "#",
    "codeUrl": "#",
    "category": "ml",
    "label": "AI/OCR Engine",
    "completedDate": "2025-05-08"
  },
  {
    "id": 5,
    "title": "Handwriting Recognition System",
    "slug": "handwriting-recognition",
    "description": "Designed and implemented a sophisticated handwriting recognition system for NOTEorious Notes, featuring seamless toggle between typing and handwriting modes. Built custom gesture detection algorithms using Flutter's touch framework and a highly optimized canvas painter to capture natural handwriting strokes in real-time. The system processes handwritten input through advanced ML/OCR models, converting handwriting to editable text with minimal latency and high accuracy across various writing styles and languages.",
    "image": "/optimized/Handwriting-md.webp",
    "tags": [
      "Flutter",
      "ML",
      "OCR",
      "Canvas API",
      "Gesture Detection",
      "Mobile UI"
    ],
    "demoUrl": "#",
    "codeUrl": "#",
    "category": "ml",
    "label": "ML/Handwriting",
    "completedDate": "2025-02-10"
  },
  {
    "id": 6,
    "title": "AI Template Generator",
    "slug": "ai-template-generator",
    "description": "Designed and built an intelligent AI Template Generator for the Lifely app, featuring OpenAI-powered template selection and dynamic content generation. Users describe their desired template through natural language prompts, and the system leverages advanced AI to intelligently select optimal prebuilt HTML templates and generate customized content. The system dynamically processes user input, fills template placeholders with AI-generated content, and renders interactive templates in WebView with full annotation capabilities for drawing, text editing, and personalization.",
    "image": "/optimized/TemplateGen-md.webp",
    "tags": [
      "OpenAI API",
      "WebView",
      "React Native",
      "Template Engine",
      "AI",
      "HTML/CSS"
    ],
    "demoUrl": "#",
    "codeUrl": "#",
    "category": "ml",
    "label": "AI/Template Engine",
    "completedDate": "2025-03-16"
  },
  {
    "id": 7,
    "title": "Animated Background System",
    "slug": "animated-background-system",
    "description": "Developed a fully custom animated background for the entire NOTEorious app, featuring a dynamic grid of floating, rotating circles. Built using Flutter's animation framework, the system uses randomized circle properties (position, size, opacity, color) generated with Dart's math library for a natural, non-repetitive look. Animation controllers drive smooth rotation and vertical floating effects, while a custom painter (CircleGridPainter) renders the animated background based on real-time animation values. This creates a modern, lively aesthetic that enhances the visual experience of the sign-up screen.",
    "image": "/optimized/Animated-md.webp",
    "tags": [
      "Flutter",
      "Animation",
      "Custom Painter",
      "UI/UX",
      "Dart",
      "Performance"
    ],
    "demoUrl": "#",
    "codeUrl": "#",
    "category": "web",
    "label": "UI Animation",
    "completedDate": "2025-04-20"
  },
  {
    "id": 8,
    "title": "IP Logging System",
    "slug": "ip-logging-system",
    "description": "Implemented a real-time IP logging system using Python client-server scripts. Integrated it into a Flutter app to capture and report device IPs for monitoring and analytics.",
    "image": "/optimized/IP-md.webp",
    "tags": [
      "Node.js",
      "Express",
      "Security",
      "API",
      "Database",
      "Monitoring"
    ],
    "demoUrl": "#",
    "codeUrl": "#",
    "category": "web",
    "label": "Backend/Security",
    "completedDate": "2025-05-10"
  },
  {
    "id": 9,
    "title": "Neural Network Hardware Accelerator",
    "slug": "neural-network-accelerator",
    "description": "Designed a custom hardware accelerator for neural network inference, optimizing for low power consumption and high throughput in edge devices.",
    "image": "/optimized/NHA-md.webp",
    "tags": [
      "VHDL",
      "Hardware",
      "Neural Networks",
      "FPGA",
      "Verilog",
      "Digital Design"
    ],
    "demoUrl": "#",
    "codeUrl": "#",
    "category": "hardware",
    "label": "Hardware/AI",
    "completedDate": "2024-12-22"
  },
  {
    "id": 10,
    "title": "Natural Language Processing API",
    "slug": "nlp-api",
    "description": "Developed a comprehensive RESTful API service for advanced natural language processing tasks, featuring multi-model sentiment analysis, named entity recognition, automated text summarization, and real-time language detection. Built with Python and modern NLP frameworks including spaCy, NLTK, and Transformers, the API provides enterprise-grade text analysis capabilities with high throughput processing, intelligent caching, and scalable microservices architecture. The system supports multiple languages, real-time processing, and provides detailed analytics for text analysis workflows across various domains including social media monitoring, content analysis, and document processing.",
    "image": "/optimized/NLP-md.webp",
    "tags": [
      "Python",
      "Flask",
      "NLP",
      "Machine Learning",
      "API",
      "Text Processing"
    ],
    "demoUrl": "https://talkhaled.com/nlp-demo",
    "codeUrl": "https://github.com/tkkalkhaled/nlp-api",
    "category": "ml",
    "label": "NLP/API",
    "completedDate": "2025-03-19"
  },
  {
    "id": 11,
    "title": "Interactive Terminal System with Realistic Human Typing Simulation",
    "slug": "interactive-terminal-system",
    "description": "Developed a sophisticated interactive terminal simulation system that demonstrates advanced frontend engineering through mathematically-modeled human typing patterns, comprehensive file system emulation, and real-time command processing. This innovative component features realistic typo generation based on keyboard proximity mapping, intelligent error correction with human-like timing delays, and a fully functional Unix-style terminal environment. Built with React and TypeScript, the system showcases complex state management, advanced animation techniques, and meticulous attention to user experience details that create an authentic developer environment simulation.",
    "image": "/Terminal_Project.png",
    "tags": [
      "React",
      "TypeScript",
      "Terminal Simulation",
      "Animation",
      "Interactive UI"
    ],
    "demoUrl": "https://talkhaled.com/project/interactive-terminal-system",
    "codeUrl": "https://github.com/tkkalkhaled/Talkhaled.com",
    "category": "web",
    "label": "Interactive Systems",
    "completedDate": "2025-06-27"
  }
];
